import { useState } from "react";
import type { CoachPriorityItem, CoachPrioritySummary } from "../coachPriority";

type CoachPrioritiesCardProps = {
  summary: CoachPrioritySummary;
};

function titleCase(value: string): string {
  return value.split("_").map(part => part.charAt(0).toUpperCase() + part.slice(1)).join(" ");
}

function PriorityRow({ item, sectionId }: { item: CoachPriorityItem; sectionId: string }) {
  const [expanded, setExpanded] = useState(false);
  const rationaleId = `${sectionId}-${item.observationId}-rationale`;

  return (
    <article className="coach-priority-row">
      <div className="coach-priority-row-header">
        <div>
          <h4>{item.headline}</h4>
          <p>{item.explanation}</p>
        </div>
        <div className="coach-priority-tags">
          <span className={`confidence-${item.confidence}`}>{titleCase(item.confidence)} confidence</span>
          <span>Score {item.score}</span>
        </div>
      </div>
      <p className="coach-priority-action"><strong>Action:</strong> {item.action}</p>
      <button
        type="button"
        className="coach-priority-rationale-toggle"
        aria-expanded={expanded}
        aria-controls={rationaleId}
        onClick={() => setExpanded(current => !current)}
      >
        Why this was prioritized
      </button>
      {expanded ? (
        <div id={rationaleId} className="coach-priority-rationale">
          <p>{item.rationale}</p>
          <p>
            Evidence {Math.round(item.scoreComponents.evidenceStrength)} · Magnitude {Math.round(item.scoreComponents.magnitude)} · Relevance {Math.round(item.scoreComponents.athleteRelevance)} · Uncertainty {Math.round(item.scoreComponents.uncertainty)}
          </p>
        </div>
      ) : null}
    </article>
  );
}

export function CoachPrioritiesCard({ summary }: CoachPrioritiesCardProps) {
  const [limitationsExpanded, setLimitationsExpanded] = useState(false);
  const limitationsDisclosureId = "coach-priority-limitations-list";

  return (
    <section className="coach-priority-card" aria-labelledby="coach-priority-title">
      <div className="coach-priority-header">
        <div>
          <p className="eyebrow">COACH PRIORITIES</p>
          <h2 id="coach-priority-title">What deserves attention first</h2>
        </div>
        <div className="coach-priority-badges">
          <span className="coach-summary-badge">Evidence-based</span>
          <span className={`coach-summary-badge confidence-${summary.overallConfidence}`}>{titleCase(summary.overallConfidence)} confidence</span>
        </div>
      </div>

      <div className="coach-priority-section">
        <h3>Most Important Finding</h3>
        {summary.mostImportantFinding ? (
          <PriorityRow item={summary.mostImportantFinding} sectionId="most-important" />
        ) : (
          <p className="command-note">No single finding required immediate intervention for this session.</p>
        )}
      </div>

      {summary.watchNext.length > 0 ? (
        <div className="coach-priority-section">
          <h3>Watch Next</h3>
          <div className="coach-priority-stack">
            {summary.watchNext.map(item => <PriorityRow key={item.observationId} item={item} sectionId="watch-next" />)}
          </div>
        </div>
      ) : null}

      {summary.noActionNeeded.length > 0 ? (
        <div className="coach-priority-section">
          <h3>No Action Needed</h3>
          <div className="coach-priority-stack">
            {summary.noActionNeeded.map(item => <PriorityRow key={item.observationId} item={item} sectionId="no-action" />)}
          </div>
        </div>
      ) : null}

      {summary.limitations.length > 0 ? (
        <div className="coach-priority-limitations">
          <h3>Data limitations</h3>
          <button
            type="button"
            className="coach-priority-limitations-toggle"
            aria-expanded={limitationsExpanded}
            aria-controls={limitationsDisclosureId}
            aria-label="Toggle data limitations"
            onClick={() => setLimitationsExpanded(current => !current)}
          >
            {summary.limitations.length} limitations available
          </button>
          {limitationsExpanded ? (
            <ul id={limitationsDisclosureId}>
              {summary.limitations.map(item => <li key={item}>{item}</li>)}
            </ul>
          ) : null}
        </div>
      ) : (
        <div className="coach-priority-limitations">
          <h3>Data limitations</h3>
          <p>No material data limitations.</p>
        </div>
      )}
    </section>
  );
}
